/**
 * Motor Vehicle Record (MVR) Service
 * Multi-state DMV integration service for Title and Registration lookups
 *
 * Supported states are dynamically fetched from the backend /api/state-dmv/states endpoint
 */

import { makeAuthenticatedRequest, createApiError, getErrorMessage } from './apiService';
import { API_ENDPOINTS } from '../config/api';

// ============================================================================
// STATE NAME/CODE MAPPINGS (50 US States)
// ============================================================================

/**
 * State code to full name mapping
 */
export const stateNames: { [key: string]: string } = {
  "AL": "Alabama", "AK": "Alaska", "AZ": "Arizona", "AR": "Arkansas", "CA": "California",
  "CO": "Colorado", "CT": "Connecticut", "DE": "Delaware", "FL": "Florida", "GA": "Georgia",
  "HI": "Hawaii", "ID": "Idaho", "IL": "Illinois", "IN": "Indiana", "IA": "Iowa",
  "KS": "Kansas", "KY": "Kentucky", "LA": "Louisiana", "ME": "Maine", "MD": "Maryland",
  "MA": "Massachusetts", "MI": "Michigan", "MN": "Minnesota", "MS": "Mississippi", "MO": "Missouri",
  "MT": "Montana", "NE": "Nebraska", "NV": "Nevada", "NH": "New Hampshire", "NJ": "New Jersey",
  "NM": "New Mexico", "NY": "New York", "NC": "North Carolina", "ND": "North Dakota", "OH": "Ohio",
  "OK": "Oklahoma", "OR": "Oregon", "PA": "Pennsylvania", "RI": "Rhode Island", "SC": "South Carolina",
  "SD": "South Dakota", "TN": "Tennessee", "TX": "Texas", "UT": "Utah", "VT": "Vermont",
  "VA": "Virginia", "WA": "Washington", "WV": "West Virginia", "WI": "Wisconsin", "WY": "Wyoming"
};

/**
 * State full name to code mapping (reverse of stateNames)
 */
export const stateCodes: { [key: string]: string } = Object.entries(stateNames).reduce(
  (acc, [code, name]) => ({ ...acc, [name]: code }),
  {} as { [key: string]: string }
);

/**
 * Convert state name to state code
 */
export const stateNameToCode = (stateName: string): string | undefined => {
  return stateCodes[stateName];
};

/**
 * Convert state code to state name
 */
export const stateCodeToName = (stateCode: string): string | undefined => {
  return stateNames[stateCode];
};

// ============================================================================
// STATE CAPABILITIES (Dynamic from Backend)
// ============================================================================

/**
 * State service capabilities as returned by the backend
 */
export interface StateCapabilities {
  state: string;           // State name (e.g., "Idaho", "Maine")
  supportsTitle: boolean;
  supportsRegistration: boolean;
  supportsDriverHistory: boolean;
  // authType and mode were removed from the API on 2026-08-20 — how a state is reached and
  // whether it runs against test or production are ours, not the caller's. Nothing read them.
  hasSeparatePricing?: boolean; // If false, single price applies to both Title and Registration
  supportsCombinedSearch?: boolean; // If true, use /combined endpoint (single portal call for both)
  supportsRegistrationAlone?: boolean; // False: Registration is sold only with the title (New Jersey)
}

/**
 * Response from /api/state-dmv/states endpoint
 */
interface SupportedStatesResponse {
  success: boolean;
  states: StateCapabilities[];
}

// Module-level cache for supported states (avoids re-fetching)
let cachedStates: StateCapabilities[] | null = null;
let cacheTimestamp: number = 0;
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Fetch supported states from the backend
 * Results are cached for 5 minutes to avoid excessive API calls
 */
export const fetchSupportedStates = async (): Promise<StateCapabilities[]> => {
  // Return cached data if still valid
  if (cachedStates && (Date.now() - cacheTimestamp) < CACHE_TTL_MS) {
    return cachedStates;
  }

  try {
    const response = await makeAuthenticatedRequest(API_ENDPOINTS.STATE_DMV.STATES, {
      method: 'GET',
    });

    if (!response.ok) {
      console.error('Failed to fetch supported states:', response.status);
      return cachedStates || [];
    }

    const data: SupportedStatesResponse = await response.json();

    if (data.success && Array.isArray(data.states)) {
      cachedStates = data.states;
      cacheTimestamp = Date.now();
      return data.states;
    }

    return cachedStates || [];
  } catch (error) {
    console.error('Error fetching supported states:', error);
    return cachedStates || [];
  }
};

/**
 * Get capabilities for a specific state by state code
 */
export const getStateCapabilities = async (stateCode: string): Promise<StateCapabilities | null> => {
  const states = await fetchSupportedStates();
  const stateName = stateCodeToName(stateCode);
  if (!stateName) return null;
  return states.find(s => s.state === stateName) || null;
};

/**
 * Check if a state has MVR integration available (async version)
 */
export const isStateSupportedAsync = async (stateCode: string): Promise<boolean> => {
  const capabilities = await getStateCapabilities(stateCode);
  return capabilities !== null;
};

/**
 * Check if a state is supported using cached data (sync version)
 * Returns false if cache is not yet populated
 */
export const isStateSupported = (stateCode: string): boolean => {
  if (!cachedStates) return false;
  const stateName = stateCodeToName(stateCode);
  if (!stateName) return false;
  return cachedStates.some(s => s.state === stateName);
};

/**
 * Get list of supported state codes (sync version using cache)
 */
export const getSupportedStateCodes = (): string[] => {
  if (!cachedStates) return [];
  return cachedStates
    .map(s => stateNameToCode(s.state))
    .filter((code): code is string => code !== undefined);
};

/**
 * Get display name for a supported state
 */
export const getStateDisplayName = (stateCode: string): string => {
  return stateNames[stateCode] || stateCode;
};

/**
 * Clear the cached states (useful for testing or forcing refresh)
 */
export const clearStatesCache = (): void => {
  cachedStates = null;
  cacheTimestamp = 0;
};

// ============================================================================
// GENERIC MVR INTERFACES (State-agnostic)
// ============================================================================

/**
 * Generic entity (owner, lienholder) - normalized across all states
 */
export interface MvrEntity {
  name?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  zip?: string;
  county?: string;
  country?: string;
  mailingLine1?: string;
  mailingLine2?: string;
  mailingCity?: string;
  mailingState?: string;
  mailingZip?: string;
  mailingCounty?: string;
  mailingCountry?: string;
  conjunction?: string;
  lienDate?: string; // For lienholder entities
  lienNumber?: string; // Lien number/reference
  lienNotation?: string; // For lienholder entities (Nebraska)
  lienELT?: string; // Electronic Lien Title flag (Nebraska)
}

/**
 * Direct lien record (Arkansas)
 */
export interface MvrDirectLien {
  lienholderName?: string;
  lienholderAddress?: string;
  lienholderCity?: string;
  lienholderState?: string;
  lienholderZip?: string;
  contractDate?: string;
  createDate?: string;
  payoffDate?: string;
  releaseDate?: string;
  filingNumber?: string;
  lienYear?: number;
  letterOfPermission?: boolean;
  debtorName?: string;
  debtorAddress?: string;
  debtorCity?: string;
  debtorState?: string;
  debtorZip?: string;
}

export interface MvrNoticeOfLien {
  ownerName?: string;
  ownerAddress?: string;
  ownerCity?: string;
  ownerState?: string;
  ownerZip?: string;
  controlNumber?: string;
  purchaseDate?: string;
  receiptDate?: string;
  lienDate?: string;
  lienholderName?: string;
  lienholderAddress?: string;
  lienholderCity?: string;
  lienholderState?: string;
  lienholderZip?: string;
  subjectToFutureAdvances?: boolean;
  refinanceOrNoChange?: boolean;
}

export interface MvrNoticeOfSale {
  ownerName?: string;
  ownerAddress?: string;
  ownerCity?: string;
  ownerState?: string;
  ownerZip?: string;
  purchaseDate?: string;
  controlNumber?: string;
}

/**
 * Generic title record - normalized across all states
 */
export interface MvrTitleRecord {
  titleNumber?: string;
  vins: string[];
  owners: MvrEntity[];
  lienHolders: MvrEntity[];
  dealership?: string;
  titlePrintDate?: string;
  titleIssueDate?: string;
  titleApplicationDate?: string;
  isReleaseOfLiability: boolean;
  jointOwnership?: boolean;
  status?: string;
  statusDescription?: string;
  year?: number;
  make?: string;
  model?: string;
  body?: string;
  color?: string;
  weight?: number;
  length?: number;
  width?: number;
  odometerReading?: string;
  odometerStatus?: string;
  odometerBrand?: string;
  odometerDate?: string;
  brands: string[];
  // Tax information (Kentucky, etc.)
  taxOwed?: boolean;
  taxableValue?: number;
  // Vehicle type info
  vehicleType?: string;
  cylinders?: number;
  fuelType?: string;
  equipmentNumber?: string;
  fleetNumber?: string;
  purchaseDate?: string;
  enteredDate?: string;
  comments?: string;
  // Insurance info
  insuranceCompany?: string;
  insurancePolicyNumber?: string;
  insuranceCommenceDate?: string;
  insuranceExpirationDate?: string;
  // Direct lien (Arkansas)
  directLien?: MvrDirectLien;
  // Notice of Lien / Notice of Sale (Missouri)
  noticeOfLien?: MvrNoticeOfLien;
  noticeOfSale?: MvrNoticeOfSale;
  // Watercraft-specific
  propulsion?: string;
  watercraftUse?: string;
  watercraftDescription?: string;
  emptyWeight?: number;
  isWatercraft?: boolean;
}

/**
 * Generic registration record - normalized across all states
 */
export interface MvrRegistrationRecord {
  plate?: string;
  plateType?: string;
  plateSeriesYear?: string;
  specialProgram?: string;
  titleNumber?: string;
  vins: string[];
  owners: MvrEntity[];
  residenceCounty?: string;
  issueDate?: string;
  expirationDate?: string;
  status?: string;
  statusDescription?: string;
  year?: number;
  make?: string;
  model?: string;
  vehicleType?: string;
  registrationType?: string;
  registrationUseType?: string;
  grossWeight?: number;
  body?: string;
  color?: string;
  // Insurance info
  insuranceCompany?: string;
  insurancePolicyNumber?: string;
  insuranceCommenceDate?: string;
  insuranceExpirationDate?: string;
}

/**
 * Generic title search response - normalized across all states
 */
export interface MvrTitleResponse {
  reportId?: string;
  searchId: string;
  vin?: string;
  titleNumber?: string;
  stateCode: string;
  fetchedAt: string;
  wasCached: boolean;
  billingAmount: number;
  success: boolean;
  titles: MvrTitleRecord[];
  errorMessage?: string;
  errorCode?: string;
  /** Something the state told us on a response that did NOT fail. Never render this as an error. */
  statusMessage?: string;
  /** Set when the state says the current record now lives in a different state. */
  recordMovedToState?: string;
}

/**
 * Generic registration search response - normalized across all states
 */
export interface MvrRegistrationResponse {
  reportId?: string;
  searchId: string;
  vin?: string;
  plate?: string;
  stateCode: string;
  fetchedAt: string;
  wasCached: boolean;
  billingAmount: number;
  success: boolean;
  registrations: MvrRegistrationRecord[];
  errorMessage?: string;
  errorCode?: string;
  /** Something the state told us on a response that did NOT fail. Never render this as an error. */
  statusMessage?: string;
  /** Set when the state says the current record now lives in a different state. */
  recordMovedToState?: string;
}

/**
 * Search request for title lookups
 */
export interface TitleSearchRequest {
  vin?: string;
  titleNumber?: string;
}

/**
 * Search request for registration lookups
 */
export interface RegistrationSearchRequest {
  vin?: string;
  plate?: string;
}

// ============================================================================
// API FUNCTIONS
// ============================================================================

/**
 * Search for vehicle title records by VIN and/or title number
 * Routes to state-specific endpoints based on the state
 */
export const searchTitle = async (
  stateCode: string,
  request: TitleSearchRequest
): Promise<MvrTitleResponse> => {
  const stateName = stateCodeToName(stateCode);

  if (!stateName) {
    return {
      searchId: crypto.randomUUID(),
      stateCode,
      fetchedAt: new Date().toISOString(),
      wasCached: false,
      billingAmount: 0,
      success: false,
      titles: [],
      errorMessage: `Invalid state code: ${stateCode}`,
      errorCode: 'INVALID_STATE_CODE',
    };
  }

  // Check if state is supported
  const capabilities = await getStateCapabilities(stateCode);
  if (!capabilities) {
    return {
      searchId: crypto.randomUUID(),
      stateCode,
      fetchedAt: new Date().toISOString(),
      wasCached: false,
      billingAmount: 0,
      success: false,
      titles: [],
      errorMessage: `MVR integration not available for state: ${stateName}`,
      errorCode: 'STATE_NOT_SUPPORTED',
    };
  }

  if (!capabilities.supportsTitle) {
    return {
      searchId: crypto.randomUUID(),
      stateCode,
      fetchedAt: new Date().toISOString(),
      wasCached: false,
      billingAmount: 0,
      success: false,
      titles: [],
      errorMessage: `Title search not available for state: ${stateName}`,
      errorCode: 'SERVICE_NOT_AVAILABLE',
    };
  }

  try {
    // Use state-specific endpoints and response handling
    // Idaho uses the legacy endpoint which returns the expected MvrTitleRecord format
    if (stateCode === 'ID') {
      const response = await makeAuthenticatedRequest(API_ENDPOINTS.IDAHO_P2P.TITLE_SEARCH, {
        method: 'POST',
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        const error = await createApiError(response);
        throw error;
      }

      const data = await response.json();
      return {
        ...data,
        stateCode,
        titles: data.titles || [],
      } as MvrTitleResponse;
    }

    // For other states, use the multi-state DMV endpoint
    // Response needs to be transformed to match MvrTitleRecord format
    const params = new URLSearchParams();
    if (request.vin) params.append('vin', request.vin);
    if (request.titleNumber) params.append('titleNumber', request.titleNumber);

    const url = `${API_ENDPOINTS.STATE_DMV.TITLE(stateName)}?${params.toString()}`;

    const response = await makeAuthenticatedRequest(url, {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await createApiError(response);
      throw error;
    }

    const data = await response.json();

    // Transform StateDMVTitleRecord to MvrTitleRecord format
    const transformedTitles: MvrTitleRecord[] = (data.titles || []).map((record: StateDMVTitleRecord) => {
      // Build lienHolders array from both lien1 and lien2
      const lienHolders: MvrEntity[] = [];
      if (record.lienholderName) {
        lienHolders.push({
          name: record.lienholderName,
          addressLine1: record.lienholderAddress,
          city: record.lienholderCity,
          state: record.lienholderState,
          zip: record.lienholderZip,
          county: record.lienholderCounty,
          lienNumber: record.lienNumber,
          lienDate: record.lienDate,
          lienNotation: record.lienNotation,
          lienELT: record.lienELT,
        });
      }
      if (record.lien2Name) {
        lienHolders.push({
          name: record.lien2Name,
          addressLine1: record.lien2Address,
          city: record.lien2City,
          state: record.lien2State,
          zip: record.lien2Zip,
          lienNumber: record.lien2Number,
          lienDate: record.lien2Date,
        });
      }

      return {
        titleNumber: record.titleNumber,
        vins: record.vin ? [record.vin] : [],
        owners: record.ownerName ? [{
          name: record.ownerName,
          addressLine1: record.ownerAddress,
          city: record.ownerCity,
          state: record.ownerState,
          zip: record.ownerZip,
          county: record.ownerCounty,
        }] : [],
        lienHolders,
        titleIssueDate: record.titleDate,
        isReleaseOfLiability: false,
        jointOwnership: record.jointOwnership,
        status: record.titleStatus,
        year: record.year,
        make: record.make,
        model: record.model,
        body: record.bodyStyle,
        color: record.color,
        odometerReading: record.odometerReading?.toString(),
        odometerStatus: record.odometerStatus,
        odometerBrand: record.odometerBrand,
        odometerDate: record.odometerDate,
        brands: record.brands || [],
        // Tax information
        taxOwed: record.taxOwed,
        taxableValue: record.taxableValue,
        // Vehicle type info
        vehicleType: record.vehicleType,
        cylinders: record.cylinders,
        fuelType: record.fuelType,
        equipmentNumber: record.equipmentNumber,
        fleetNumber: record.fleetNumber,
        purchaseDate: record.purchaseDate,
        enteredDate: record.enteredDate,
        comments: record.comments,
        // Insurance info
        insuranceCompany: record.insuranceCompany,
        insurancePolicyNumber: record.insurancePolicyNumber,
        insuranceCommenceDate: record.insuranceCommenceDate,
        insuranceExpirationDate: record.insuranceExpirationDate,
        // Direct lien
        directLien: record.directLienholderName ? {
          lienholderName: record.directLienholderName,
          lienholderAddress: record.directLienholderAddress,
          lienholderCity: record.directLienholderCity,
          lienholderState: record.directLienholderState,
          lienholderZip: record.directLienholderZip,
          contractDate: record.directLienContractDate,
          createDate: record.directLienCreateDate,
          payoffDate: record.directLienPayoffDate,
          releaseDate: record.directLienReleaseDate,
          filingNumber: record.directLienFilingNumber,
          lienYear: record.directLienYear,
          letterOfPermission: record.directLienLetterOfPermission,
          debtorName: record.directLienDebtorName,
          debtorAddress: record.directLienDebtorAddress,
          debtorCity: record.directLienDebtorCity,
          debtorState: record.directLienDebtorState,
          debtorZip: record.directLienDebtorZip,
        } : undefined,
        // Notice of Lien (Missouri)
        noticeOfLien: record.noticeOfLienOwnerName || record.noticeOfLienholderName ? {
          ownerName: record.noticeOfLienOwnerName,
          ownerAddress: record.noticeOfLienOwnerAddress,
          ownerCity: record.noticeOfLienOwnerCity,
          ownerState: record.noticeOfLienOwnerState,
          ownerZip: record.noticeOfLienOwnerZip,
          controlNumber: record.noticeOfLienControlNumber,
          purchaseDate: record.noticeOfLienPurchaseDate,
          receiptDate: record.noticeOfLienReceiptDate,
          lienDate: record.noticeOfLienDate,
          lienholderName: record.noticeOfLienholderName,
          lienholderAddress: record.noticeOfLienholderAddress,
          lienholderCity: record.noticeOfLienholderCity,
          lienholderState: record.noticeOfLienholderState,
          lienholderZip: record.noticeOfLienholderZip,
          subjectToFutureAdvances: record.noticeOfLienSubjectToFutureAdvances,
          refinanceOrNoChange: record.noticeOfLienRefinanceOrNoChange,
        } : undefined,
        // Notice of Sale (Missouri)
        noticeOfSale: record.noticeOfSaleOwnerName ? {
          ownerName: record.noticeOfSaleOwnerName,
          ownerAddress: record.noticeOfSaleOwnerAddress,
          ownerCity: record.noticeOfSaleOwnerCity,
          ownerState: record.noticeOfSaleOwnerState,
          ownerZip: record.noticeOfSaleOwnerZip,
          purchaseDate: record.noticeOfSalePurchaseDate,
          controlNumber: record.noticeOfSaleControlNumber,
        } : undefined,
        // Watercraft
        propulsion: record.propulsion,
        watercraftUse: record.watercraftUse,
        watercraftDescription: record.watercraftDescription,
        emptyWeight: record.emptyWeight,
        isWatercraft: record.isWatercraft,
      };
    });

    return {
      searchId: crypto.randomUUID(),
      reportId: data.reportId,
      vin: data.vin || request.vin,
      titleNumber: data.titleNumber || request.titleNumber,
      stateCode,
      fetchedAt: new Date().toISOString(),
      wasCached: false,
      billingAmount: data.billingAmount || 0,
      success: data.success !== false,
      titles: transformedTitles,
      errorMessage: data.errorMessage,
      errorCode: data.errorCode,
      statusMessage: data.statusMessage,
      recordMovedToState: data.recordMovedToState,
    } as MvrTitleResponse;
  } catch (error) {
    throw error;
  }
};

// Types for StateDMV backend responses (different from frontend Mvr* types)
// Exported for use in log parsing
export interface StateDMVTitleRecord {
  titleNumber?: string;
  vin?: string;
  titleStatus?: string;
  titleDate?: string;
  titleType?: string;
  year?: number;
  make?: string;
  model?: string;
  bodyStyle?: string;
  color?: string;
  vehicleType?: string;
  cylinders?: number;
  fuelType?: string;
  equipmentNumber?: string;
  fleetNumber?: string;
  purchaseDate?: string;
  enteredDate?: string;
  comments?: string;
  ownerName?: string;
  ownerAddress?: string;
  ownerCity?: string;
  ownerState?: string;
  ownerZip?: string;
  ownerCounty?: string;
  jointOwnership?: boolean;
  // Lien 1 info
  lienholderName?: string;
  lienholderAddress?: string;
  lienholderCity?: string;
  lienholderState?: string;
  lienholderZip?: string;
  lienholderCounty?: string;
  lienNumber?: string;
  lienNotation?: string;
  lienELT?: string;
  lienDate?: string;
  // Lien 2 info
  lien2Name?: string;
  lien2Address?: string;
  lien2City?: string;
  lien2State?: string;
  lien2Zip?: string;
  lien2Number?: string;
  lien2Date?: string;
  // Direct Lien info
  directLienholderName?: string;
  directLienholderAddress?: string;
  directLienholderCity?: string;
  directLienholderState?: string;
  directLienholderZip?: string;
  directLienContractDate?: string;
  directLienCreateDate?: string;
  directLienPayoffDate?: string;
  directLienReleaseDate?: string;
  directLienFilingNumber?: string;
  directLienYear?: number;
  directLienLetterOfPermission?: boolean;
  directLienDebtorName?: string;
  directLienDebtorAddress?: string;
  directLienDebtorCity?: string;
  directLienDebtorState?: string;
  directLienDebtorZip?: string;
  // Notice of Lien (Missouri)
  noticeOfLienOwnerName?: string;
  noticeOfLienOwnerAddress?: string;
  noticeOfLienOwnerCity?: string;
  noticeOfLienOwnerState?: string;
  noticeOfLienOwnerZip?: string;
  noticeOfLienControlNumber?: string;
  noticeOfLienPurchaseDate?: string;
  noticeOfLienReceiptDate?: string;
  noticeOfLienDate?: string;
  noticeOfLienholderName?: string;
  noticeOfLienholderAddress?: string;
  noticeOfLienholderCity?: string;
  noticeOfLienholderState?: string;
  noticeOfLienholderZip?: string;
  noticeOfLienSubjectToFutureAdvances?: boolean;
  noticeOfLienRefinanceOrNoChange?: boolean;
  // Notice of Sale (Missouri)
  noticeOfSaleOwnerName?: string;
  noticeOfSaleOwnerAddress?: string;
  noticeOfSaleOwnerCity?: string;
  noticeOfSaleOwnerState?: string;
  noticeOfSaleOwnerZip?: string;
  noticeOfSalePurchaseDate?: string;
  noticeOfSaleControlNumber?: string;
  // Watercraft
  propulsion?: string;
  watercraftUse?: string;
  watercraftDescription?: string;
  emptyWeight?: number;
  isWatercraft?: boolean;
  // Odometer
  odometerReading?: number;
  odometerStatus?: string;
  odometerBrand?: string;
  odometerDate?: string;
  // Tax info
  taxOwed?: boolean;
  taxableValue?: number;
  brands?: string[];
  // Insurance info
  insuranceCompany?: string;
  insurancePolicyNumber?: string;
  insuranceCommenceDate?: string;
  insuranceExpirationDate?: string;
}

export interface StateDMVRegistrationRecord {
  plate?: string;
  plateType?: string;
  plateSeriesYear?: string;
  vin?: string;
  registrationStatus?: string;
  registrationDate?: string;
  expirationDate?: string;
  registrationType?: string;
  registrationUseType?: string;
  grossWeight?: number;
  year?: number;
  make?: string;
  model?: string;
  bodyStyle?: string;
  color?: string;
  ownerName?: string;
  ownerAddress?: string;
  ownerCity?: string;
  ownerState?: string;
  ownerZip?: string;
  county?: string;
}

/**
 * Search for vehicle registration records by VIN and/or plate number
 * Routes to state-specific endpoints based on the state
 */
export const searchRegistration = async (
  stateCode: string,
  request: RegistrationSearchRequest
): Promise<MvrRegistrationResponse> => {
  const stateName = stateCodeToName(stateCode);

  if (!stateName) {
    return {
      searchId: crypto.randomUUID(),
      stateCode,
      fetchedAt: new Date().toISOString(),
      wasCached: false,
      billingAmount: 0,
      success: false,
      registrations: [],
      errorMessage: `Invalid state code: ${stateCode}`,
      errorCode: 'INVALID_STATE_CODE',
    };
  }

  // Check if state is supported
  const capabilities = await getStateCapabilities(stateCode);
  if (!capabilities) {
    return {
      searchId: crypto.randomUUID(),
      stateCode,
      fetchedAt: new Date().toISOString(),
      wasCached: false,
      billingAmount: 0,
      success: false,
      registrations: [],
      errorMessage: `MVR integration not available for state: ${stateName}`,
      errorCode: 'STATE_NOT_SUPPORTED',
    };
  }

  if (!capabilities.supportsRegistration) {
    return {
      searchId: crypto.randomUUID(),
      stateCode,
      fetchedAt: new Date().toISOString(),
      wasCached: false,
      billingAmount: 0,
      success: false,
      registrations: [],
      errorMessage: `Registration search not available for state: ${stateName}`,
      errorCode: 'SERVICE_NOT_AVAILABLE',
    };
  }

  try {
    // Use state-specific endpoints and response handling
    // Idaho uses the legacy endpoint which returns the expected MvrRegistrationRecord format
    if (stateCode === 'ID') {
      const response = await makeAuthenticatedRequest(API_ENDPOINTS.IDAHO_P2P.REGISTRATION_SEARCH, {
        method: 'POST',
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        const error = await createApiError(response);
        throw error;
      }

      const data = await response.json();
      return {
        ...data,
        stateCode,
        registrations: data.registrations || [],
      } as MvrRegistrationResponse;
    }

    // For other states, use the multi-state DMV endpoint
    // Response needs to be transformed to match MvrRegistrationRecord format
    const params = new URLSearchParams();
    if (request.vin) params.append('vin', request.vin);
    if (request.plate) params.append('plate', request.plate);

    const url = `${API_ENDPOINTS.STATE_DMV.REGISTRATION(stateName)}?${params.toString()}`;

    const response = await makeAuthenticatedRequest(url, {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await createApiError(response);
      throw error;
    }

    const data = await response.json();

    // Transform StateDMVRegistrationRecord to MvrRegistrationRecord format
    const transformedRegistrations: MvrRegistrationRecord[] = (data.registrations || []).map((record: StateDMVRegistrationRecord) => ({
      plate: record.plate,
      plateType: record.plateType,
      plateSeriesYear: record.plateSeriesYear,
      vins: record.vin ? [record.vin] : [],
      owners: record.ownerName ? [{
        name: record.ownerName,
        addressLine1: record.ownerAddress,
        city: record.ownerCity,
        state: record.ownerState,
        zip: record.ownerZip,
        county: record.county,
      }] : [],
      issueDate: record.registrationDate,
      expirationDate: record.expirationDate,
      status: record.registrationStatus,
      registrationType: record.registrationType,
      registrationUseType: record.registrationUseType,
      grossWeight: record.grossWeight,
      year: record.year,
      make: record.make,
      model: record.model,
      body: record.bodyStyle,
      color: record.color,
    }));

    return {
      searchId: crypto.randomUUID(),
      reportId: data.reportId,
      vin: data.vin || request.vin,
      plate: data.plate || request.plate,
      stateCode,
      fetchedAt: new Date().toISOString(),
      wasCached: false,
      billingAmount: data.billingAmount || 0,
      success: data.success !== false,
      registrations: transformedRegistrations,
      errorMessage: data.errorMessage,
      errorCode: data.errorCode,
      statusMessage: data.statusMessage,
      recordMovedToState: data.recordMovedToState,
    } as MvrRegistrationResponse;
  } catch (error) {
    throw error;
  }
};

/**
 * Record type filter for MVR searches
 */
export type MvrRecordType = 'title' | 'registration' | 'both';

/**
 * Response from the MVR pricing endpoint
 */
export interface MvrPricingResponse {
  stateCode: string;
  hasSeparatePricing: boolean;
  titleCost: number | null;      // null for single-price states
  registrationCost: number | null; // null for single-price states
  totalCost: number;
  recordType: string;
}

/**
 * Fetch MVR pricing for a state and record type combination
 * @param stateCode - Two-letter state code
 * @param recordType - Which record types: 'title', 'registration', or 'both'
 */
export const fetchMvrPricing = async (
  stateCode: string,
  recordType: MvrRecordType = 'both'
): Promise<MvrPricingResponse> => {
  const params = new URLSearchParams({
    state: stateCode,
    recordType,
  });

  const response = await makeAuthenticatedRequest(
    `${API_ENDPOINTS.STATE_DMV.PRICING}?${params}`,
    { method: 'GET' }
  );

  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }

  return await response.json();
};

/**
 * Search both title and registration records for a VIN in a specific state
 * Returns both results for display in tabs
 * @param stateCode - Two-letter state code
 * @param vin - Vehicle Identification Number
 * @param recordType - Which record types to search: 'title', 'registration', or 'both'
 */
export const searchMVR = async (
  stateCode: string,
  vin: string,
  recordType: MvrRecordType = 'both'
): Promise<{
  titleResponse: MvrTitleResponse | null;
  registrationResponse: MvrRegistrationResponse | null;
}> => {
  // Ensure states are loaded
  await fetchSupportedStates();

  // Check if state is supported
  const capabilities = await getStateCapabilities(stateCode);
  if (!capabilities) {
    const stateName = stateCodeToName(stateCode) || stateCode;
    const errorResponse = {
      searchId: crypto.randomUUID(),
      stateCode,
      fetchedAt: new Date().toISOString(),
      wasCached: false,
      billingAmount: 0,
      success: false,
      errorMessage: `MVR integration not available for state: ${stateName}`,
      errorCode: 'STATE_NOT_SUPPORTED',
    };

    return {
      titleResponse: { ...errorResponse, titles: [] } as MvrTitleResponse,
      registrationResponse: { ...errorResponse, registrations: [] } as MvrRegistrationResponse,
    };
  }

  // Only search the requested record types
  const shouldSearchTitle = (recordType === 'title' || recordType === 'both') && capabilities.supportsTitle;
  const shouldSearchRegistration = (recordType === 'registration' || recordType === 'both') && capabilities.supportsRegistration;

  // For "both" searches, always use the combined endpoint (single activity entry)
  // Backend handles both browser automation (single portal call) and HTTP states (parallel fallback)
  if (shouldSearchTitle && shouldSearchRegistration) {
    try {
      const stateName = stateCodeToName(stateCode) || stateCode;
      const params = new URLSearchParams();
      params.append('vin', vin);

      const url = `${API_ENDPOINTS.STATE_DMV.COMBINED(stateName)}?${params.toString()}`;
      const response = await makeAuthenticatedRequest(url, { method: 'GET' });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        const errorMsg = errorData?.message || `Combined search failed with status ${response.status}`;
        const errorResponse = {
          searchId: crypto.randomUUID(),
          stateCode,
          fetchedAt: new Date().toISOString(),
          wasCached: false,
          billingAmount: 0,
          success: false,
          errorMessage: errorMsg,
          errorCode: 'COMBINED_SEARCH_FAILED',
        };
        return {
          titleResponse: { ...errorResponse, titles: [] } as MvrTitleResponse,
          registrationResponse: { ...errorResponse, registrations: [] } as MvrRegistrationResponse,
        };
      }

      const combinedData = await response.json();

      // Transform title data from StateDMV format to Mvr format
      let titleResponse: MvrTitleResponse | null = null;
      if (combinedData.titleResponse) {
        const titleData = combinedData.titleResponse;
        const transformedTitles: MvrTitleRecord[] = (titleData.titles || []).map((record: StateDMVTitleRecord) => {
          const lienHolders: MvrEntity[] = [];
          if (record.lienholderName) {
            lienHolders.push({
              name: record.lienholderName,
              addressLine1: record.lienholderAddress,
              city: record.lienholderCity,
              state: record.lienholderState,
              zip: record.lienholderZip,
              county: record.lienholderCounty,
              lienNumber: record.lienNumber,
              lienDate: record.lienDate,
              lienNotation: record.lienNotation,
              lienELT: record.lienELT,
            });
          }
          if (record.lien2Name) {
            lienHolders.push({
              name: record.lien2Name,
              addressLine1: record.lien2Address,
              city: record.lien2City,
              state: record.lien2State,
              zip: record.lien2Zip,
              lienNumber: record.lien2Number,
              lienDate: record.lien2Date,
            });
          }
          return {
            titleNumber: record.titleNumber,
            vins: record.vin ? [record.vin] : [],
            owners: record.ownerName ? [{
              name: record.ownerName,
              addressLine1: record.ownerAddress,
              city: record.ownerCity,
              state: record.ownerState,
              zip: record.ownerZip,
              county: record.ownerCounty,
            }] : [],
            lienHolders,
            titleIssueDate: record.titleDate,
            isReleaseOfLiability: false,
            status: record.titleStatus,
            year: record.year,
            make: record.make,
            model: record.model,
            body: record.bodyStyle,
            color: record.color,
            odometerReading: record.odometerReading?.toString(),
            odometerStatus: record.odometerStatus,
            odometerBrand: record.odometerBrand,
            odometerDate: record.odometerDate,
            brands: record.brands || [],
            taxOwed: record.taxOwed,
            taxableValue: record.taxableValue,
            vehicleType: record.vehicleType,
            cylinders: record.cylinders,
            equipmentNumber: record.equipmentNumber,
            fleetNumber: record.fleetNumber,
            comments: record.comments,
            insuranceCompany: record.insuranceCompany,
            insurancePolicyNumber: record.insurancePolicyNumber,
            insuranceCommenceDate: record.insuranceCommenceDate,
            insuranceExpirationDate: record.insuranceExpirationDate,
            directLien: record.directLienholderName ? {
              lienholderName: record.directLienholderName,
              lienholderAddress: record.directLienholderAddress,
              lienholderCity: record.directLienholderCity,
              lienholderState: record.directLienholderState,
              lienholderZip: record.directLienholderZip,
              contractDate: record.directLienContractDate,
              createDate: record.directLienCreateDate,
              payoffDate: record.directLienPayoffDate,
              releaseDate: record.directLienReleaseDate,
              filingNumber: record.directLienFilingNumber,
              lienYear: record.directLienYear,
              letterOfPermission: record.directLienLetterOfPermission,
              debtorName: record.directLienDebtorName,
              debtorAddress: record.directLienDebtorAddress,
              debtorCity: record.directLienDebtorCity,
              debtorState: record.directLienDebtorState,
              debtorZip: record.directLienDebtorZip,
            } : undefined,
            // Notice of Lien (Missouri)
            noticeOfLien: record.noticeOfLienOwnerName || record.noticeOfLienholderName ? {
              ownerName: record.noticeOfLienOwnerName,
              ownerAddress: record.noticeOfLienOwnerAddress,
              ownerCity: record.noticeOfLienOwnerCity,
              ownerState: record.noticeOfLienOwnerState,
              ownerZip: record.noticeOfLienOwnerZip,
              controlNumber: record.noticeOfLienControlNumber,
              purchaseDate: record.noticeOfLienPurchaseDate,
              receiptDate: record.noticeOfLienReceiptDate,
              lienDate: record.noticeOfLienDate,
              lienholderName: record.noticeOfLienholderName,
              lienholderAddress: record.noticeOfLienholderAddress,
              lienholderCity: record.noticeOfLienholderCity,
              lienholderState: record.noticeOfLienholderState,
              lienholderZip: record.noticeOfLienholderZip,
              subjectToFutureAdvances: record.noticeOfLienSubjectToFutureAdvances,
              refinanceOrNoChange: record.noticeOfLienRefinanceOrNoChange,
            } : undefined,
            // Notice of Sale (Missouri)
            noticeOfSale: record.noticeOfSaleOwnerName ? {
              ownerName: record.noticeOfSaleOwnerName,
              ownerAddress: record.noticeOfSaleOwnerAddress,
              ownerCity: record.noticeOfSaleOwnerCity,
              ownerState: record.noticeOfSaleOwnerState,
              ownerZip: record.noticeOfSaleOwnerZip,
              purchaseDate: record.noticeOfSalePurchaseDate,
              controlNumber: record.noticeOfSaleControlNumber,
            } : undefined,
            // Watercraft
            propulsion: record.propulsion,
            watercraftUse: record.watercraftUse,
            watercraftDescription: record.watercraftDescription,
            emptyWeight: record.emptyWeight,
            isWatercraft: record.isWatercraft,
          };
        });
        titleResponse = {
          searchId: crypto.randomUUID(),
          reportId: combinedData.reportId,
          vin: titleData.vin || vin,
          stateCode,
          fetchedAt: new Date().toISOString(),
          wasCached: false,
          billingAmount: titleData.billingAmount || 0,
          success: titleData.success !== false,
          titles: transformedTitles,
          errorMessage: titleData.errorMessage,
          errorCode: titleData.errorCode,
        } as MvrTitleResponse;
      }

      // Transform registration data from StateDMV format to Mvr format
      let registrationResponse: MvrRegistrationResponse | null = null;
      if (combinedData.registrationResponse) {
        const regData = combinedData.registrationResponse;
        const transformedRegistrations: MvrRegistrationRecord[] = (regData.registrations || []).map((record: StateDMVRegistrationRecord) => ({
          plate: record.plate,
          plateType: record.plateType,
          plateSeriesYear: record.plateSeriesYear,
          vins: record.vin ? [record.vin] : [],
          owners: record.ownerName ? [{
            name: record.ownerName,
            addressLine1: record.ownerAddress,
            city: record.ownerCity,
            state: record.ownerState,
            zip: record.ownerZip,
            county: record.county,
          }] : [],
          issueDate: record.registrationDate,
          expirationDate: record.expirationDate,
          status: record.registrationStatus,
          registrationType: record.registrationType,
          registrationUseType: record.registrationUseType,
          grossWeight: record.grossWeight,
          year: record.year,
          make: record.make,
          model: record.model,
          body: record.bodyStyle,
          color: record.color,
        }));
        registrationResponse = {
          searchId: crypto.randomUUID(),
          reportId: combinedData.reportId,
          vin: regData.vin || vin,
          stateCode,
          fetchedAt: new Date().toISOString(),
          wasCached: false,
          billingAmount: regData.billingAmount || 0,
          success: regData.success !== false,
          registrations: transformedRegistrations,
          errorMessage: regData.errorMessage,
          errorCode: regData.errorCode,
        } as MvrRegistrationResponse;
      }

      return { titleResponse, registrationResponse };
    } catch (error) {
      console.error('Combined search error, falling back to separate calls:', error);
      // Fall through to separate calls below
    }
  }

  const [titleResult, registrationResult] = await Promise.allSettled([
    shouldSearchTitle ? searchTitle(stateCode, { vin }) : Promise.resolve(null),
    shouldSearchRegistration ? searchRegistration(stateCode, { vin }) : Promise.resolve(null),
  ]);

  return {
    titleResponse: titleResult.status === 'fulfilled' ? titleResult.value : null,
    registrationResponse: registrationResult.status === 'fulfilled' ? registrationResult.value : null,
  };
};

// ============================================================================
// PDF GENERATION
// ============================================================================

/**
 * Request parameters for MVR PDF generation
 */
export interface MvrPdfRequest {
  vin: string;
  stateCode: string;
  titleReportId?: string;
  registrationReportId?: string;
  requestedBy?: string;
  referenceNote?: string;
  recordType?: MvrRecordType;
  dppaUse?: string;
  // VIN Decode enrichment fields (from DataOne/NHTSA)
  cylinders?: string;
  fuelType?: string;
  engineDisplacement?: string;
  horsepower?: string;
  transmissionStyle?: string;
  driveType?: string;
}

/**
 * Fetches a Motor Vehicle Record PDF as a Blob (no download).
 * Used to embed the PDF inline on the results page.
 */
export const fetchMvrPdfBlob = async (request: MvrPdfRequest): Promise<Blob> => {
  let endpoint: string;
  let fetchOptions: RequestInit;

  if (request.stateCode === 'ID') {
    endpoint = `${API_ENDPOINTS.API_BASE_URL}/api/idahop2p/generate-pdf`;
    fetchOptions = {
      method: 'POST',
      body: JSON.stringify({
        ...request,
        titleReportId: request.titleReportId ?? undefined,
        registrationReportId: request.registrationReportId ?? undefined,
      }),
    };
  } else {
    const stateName = stateCodeToName(request.stateCode);
    const params = new URLSearchParams({ vin: request.vin });
    if (request.titleReportId) params.append('titleReportId', request.titleReportId);
    if (request.registrationReportId) params.append('registrationReportId', request.registrationReportId);
    if (request.requestedBy) params.append('requestedBy', request.requestedBy);
    if (request.referenceNote) params.append('referenceNote', request.referenceNote);
    if (request.dppaUse) params.append('dppaUse', request.dppaUse);
    if (request.recordType) params.append('recordType', request.recordType);

    endpoint = `${API_ENDPOINTS.API_BASE_URL}/api/state-dmv/${stateName}/mvr-pdf?${params}`;
    fetchOptions = { method: 'GET' };
  }

  const response = await makeAuthenticatedRequest(endpoint, fetchOptions);
  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }
  return await response.blob();
};

/**
 * Generate and download a Motor Vehicle Record PDF report
 */
export const generateMvrPdf = async (request: MvrPdfRequest): Promise<void> => {
  let endpoint: string;
  let fetchOptions: RequestInit;

  if (request.stateCode === 'ID') {
    // Idaho uses the P2P endpoint (POST with JSON body)
    endpoint = `${API_ENDPOINTS.API_BASE_URL}/api/idahop2p/generate-pdf`;
    fetchOptions = {
      method: 'POST',
      body: JSON.stringify({
        ...request,
        titleReportId: request.titleReportId ?? undefined,
        registrationReportId: request.registrationReportId ?? undefined,
      }),
    };
  } else {
    // Other states use the multi-state StateDMV endpoint (GET with query params)
    const stateName = stateCodeToName(request.stateCode);
    const params = new URLSearchParams({
      vin: request.vin,
    });
    if (request.titleReportId) params.append('titleReportId', request.titleReportId);
    if (request.registrationReportId) params.append('registrationReportId', request.registrationReportId);
    if (request.requestedBy) params.append('requestedBy', request.requestedBy);
    if (request.referenceNote) params.append('referenceNote', request.referenceNote);
    if (request.dppaUse) params.append('dppaUse', request.dppaUse);
    if (request.recordType) params.append('recordType', request.recordType);

    endpoint = `${API_ENDPOINTS.API_BASE_URL}/api/state-dmv/${stateName}/mvr-pdf?${params}`;
    fetchOptions = {
      method: 'GET',
    };
  }

  try {
    const response = await makeAuthenticatedRequest(endpoint, fetchOptions);

    if (!response.ok) {
      const error = await createApiError(response);
      throw error;
    }

    // Get the PDF blob
    const blob = await response.blob();

    // Create filename from response headers or generate one
    const contentDisposition = response.headers.get('content-disposition');
    let filename = `MVR-Report-${request.vin}-${request.stateCode}-${new Date().toISOString().slice(0, 10)}.pdf`;
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '');
      }
    }

    // Create download link and trigger download
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('MVR PDF generation failed:', error);
    throw error;
  }
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Format date for display (Mon DD, YYYY)
 */
export const formatMvrDate = (dateString?: string): string => {
  if (!dateString || dateString.startsWith('0001-01-01')) return '';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime()) || date.getFullYear() <= 1) return '';
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return dateString;
  }
};

/**
 * Format odometer reading with commas
 */
export const formatOdometer = (reading?: string): string => {
  if (!reading) return 'N/A';
  const num = parseInt(reading, 10);
  if (isNaN(num)) return reading;
  return `${num.toLocaleString()} miles`;
};

/**
 * Get short report ID (RPT-XXXXXX)
 */
export const getShortReportId = (reportId?: string): string => {
  if (!reportId) return 'N/A';
  return `RPT-${reportId.substring(0, 6).toUpperCase()}`;
};

/**
 * Check if a registration is expired
 */
export const isRegistrationExpired = (expirationDate?: string): boolean => {
  if (!expirationDate) return false;
  try {
    return new Date(expirationDate) < new Date();
  } catch {
    return false;
  }
};

/**
 * Calculate summary statistics for title records
 */
export const calculateTitleSummary = (titles: MvrTitleRecord[]) => {
  const totalOwners = titles.reduce((sum, t) => sum + (t.owners?.length || 0), 0);
  const totalLiens = titles.reduce((sum, t) => sum + (t.lienHolders?.length || 0), 0);
  const hasLiens = totalLiens > 0;
  const totalDirectLiens = titles.filter(t => t.directLien).length;
  const hasDirectLiens = totalDirectLiens > 0;
  const totalNoticeOfLien = titles.filter(t => t.noticeOfLien).length;
  const hasNoticeOfLien = totalNoticeOfLien > 0;
  const totalNoticeOfSale = titles.filter(t => t.noticeOfSale).length;
  const hasNoticeOfSale = totalNoticeOfSale > 0;

  return {
    titleCount: titles.length,
    totalOwners,
    totalLiens,
    hasLiens,
    totalDirectLiens,
    hasDirectLiens,
    totalNoticeOfLien,
    hasNoticeOfLien,
    totalNoticeOfSale,
    hasNoticeOfSale,
  };
};

/**
 * Calculate summary statistics for registration records
 */
export const calculateRegistrationSummary = (registrations: MvrRegistrationRecord[]) => {
  const totalOwners = registrations.reduce((sum, r) => sum + (r.owners?.length || 0), 0);
  const activeCount = registrations.filter(
    r => r.status?.toLowerCase() === 'active' && !isRegistrationExpired(r.expirationDate)
  ).length;
  const expiredCount = registrations.filter(
    r => isRegistrationExpired(r.expirationDate)
  ).length;
  const hasExpired = expiredCount > 0;

  return {
    registrationCount: registrations.length,
    totalOwners,
    activeCount,
    expiredCount,
    hasExpired,
  };
};

// ============================================================================
// LOG RESPONSE PARSING
// Transform stored API log responses back into frontend format
// ============================================================================

/**
 * Backend response structure for title searches (stored in API logs)
 */
interface StoredTitleResponse {
  success: boolean;
  searchId?: string;
  reportId?: string;
  vin?: string;
  titleNumber?: string;
  stateCode?: string;
  fetchedAt?: string;
  wasCached?: boolean;
  billingAmount?: number;
  errorMessage?: string;
  errorCode?: string;
  titles?: StateDMVTitleRecord[];
}

/**
 * Backend response structure for registration searches (stored in API logs)
 */
interface StoredRegistrationResponse {
  success: boolean;
  searchId?: string;
  reportId?: string;
  vin?: string;
  plate?: string;
  stateCode?: string;
  fetchedAt?: string;
  wasCached?: boolean;
  billingAmount?: number;
  errorMessage?: string;
  errorCode?: string;
  registrations?: StateDMVRegistrationRecord[];
}

/**
 * Transform a stored title response from API logs into frontend MvrTitleResponse format
 */
export const transformStoredTitleResponse = (
  data: StoredTitleResponse,
  stateCode: string
): MvrTitleResponse => {
  // Transform title records — handles both formats:
  //   Flat StateDMVTitleRecord (new): ownerName, titleDate, titleStatus, bodyStyle, lienholderName, ...
  //   Nested IdahoP2PTitleObject (legacy): owners[], lienHolders[], titleIssueDate, statusDescription, body, ...
  const transformedTitles: MvrTitleRecord[] = (data.titles || []).map((record: any) => {
    const isLegacyIdaho = Array.isArray(record.owners);

    // Build lienHolders array
    let lienHolders: MvrEntity[];
    if (isLegacyIdaho) {
      lienHolders = (record.lienHolders || []).map((l: any) => ({
        name: l.name,
        addressLine1: l.mailingLine1 || l.addressLine1,
        city: l.mailingCity || l.city,
        state: l.mailingState || l.state,
        zip: l.mailingZip || l.zip,
      }));
    } else {
      lienHolders = [];
      if (record.lienholderName) {
        lienHolders.push({
          name: record.lienholderName,
          addressLine1: record.lienholderAddress,
          city: record.lienholderCity,
          state: record.lienholderState,
          zip: record.lienholderZip,
          county: record.lienholderCounty,
          lienNumber: record.lienNumber,
          lienDate: record.lienDate,
          lienNotation: record.lienNotation,
          lienELT: record.lienELT,
        });
      }
      if (record.lien2Name) {
        lienHolders.push({
          name: record.lien2Name,
          addressLine1: record.lien2Address,
          city: record.lien2City,
          state: record.lien2State,
          zip: record.lien2Zip,
          lienNumber: record.lien2Number,
          lienDate: record.lien2Date,
        });
      }
    }

    // Build owners array
    const owners: MvrEntity[] = isLegacyIdaho
      ? (record.owners || []).map((o: any) => ({
          name: o.name,
          addressLine1: o.mailingLine1 || o.addressLine1,
          city: o.mailingCity || o.city,
          state: o.mailingState || o.state,
          zip: o.mailingZip || o.zip,
          county: o.county,
          conjunction: o.conjunction,
        }))
      : record.ownerName ? [{
          name: record.ownerName,
          addressLine1: record.ownerAddress,
          city: record.ownerCity,
          state: record.ownerState,
          zip: record.ownerZip,
          county: record.ownerCounty,
        }] : [];

    return {
      titleNumber: record.titleNumber,
      vins: isLegacyIdaho ? (record.vins || []) : (record.vin ? [record.vin] : []),
      owners,
      lienHolders,
      titleIssueDate: isLegacyIdaho ? record.titleIssueDate : record.titleDate,
      titleApplicationDate: isLegacyIdaho ? record.titleApplicationDate : record.purchaseDate,
      isReleaseOfLiability: record.isReleaseOfLiability || false,
      status: isLegacyIdaho ? (record.statusDescription || record.status) : record.titleStatus,
      statusDescription: isLegacyIdaho ? record.statusDescription : record.titleStatus,
      year: record.year,
      make: record.make,
      model: record.model,
      body: isLegacyIdaho ? record.body : record.bodyStyle,
      color: record.color,
      odometerReading: record.odometerReading?.toString(),
      odometerStatus: record.odometerStatus,
      odometerBrand: record.odometerBrand,
      odometerDate: record.odometerDate,
      brands: record.brands || [],
      // Tax information
      taxOwed: record.taxOwed,
      taxableValue: record.taxableValue,
      // Vehicle type info
      vehicleType: record.vehicleType,
      cylinders: record.cylinders,
      equipmentNumber: record.equipmentNumber,
      fleetNumber: record.fleetNumber,
      comments: record.comments,
      // Insurance info
      insuranceCompany: record.insuranceCompany,
      insurancePolicyNumber: record.insurancePolicyNumber,
      insuranceCommenceDate: record.insuranceCommenceDate,
      insuranceExpirationDate: record.insuranceExpirationDate,
      // Direct lien
      directLien: record.directLienholderName ? {
        lienholderName: record.directLienholderName,
        lienholderAddress: record.directLienholderAddress,
        lienholderCity: record.directLienholderCity,
        lienholderState: record.directLienholderState,
        lienholderZip: record.directLienholderZip,
        contractDate: record.directLienContractDate,
        createDate: record.directLienCreateDate,
        payoffDate: record.directLienPayoffDate,
        releaseDate: record.directLienReleaseDate,
        filingNumber: record.directLienFilingNumber,
        lienYear: record.directLienYear,
        letterOfPermission: record.directLienLetterOfPermission,
        debtorName: record.directLienDebtorName,
        debtorAddress: record.directLienDebtorAddress,
        debtorCity: record.directLienDebtorCity,
        debtorState: record.directLienDebtorState,
        debtorZip: record.directLienDebtorZip,
      } : undefined,
      // Notice of Lien (Missouri)
      noticeOfLien: record.noticeOfLienOwnerName || record.noticeOfLienholderName ? {
        ownerName: record.noticeOfLienOwnerName,
        ownerAddress: record.noticeOfLienOwnerAddress,
        ownerCity: record.noticeOfLienOwnerCity,
        ownerState: record.noticeOfLienOwnerState,
        ownerZip: record.noticeOfLienOwnerZip,
        controlNumber: record.noticeOfLienControlNumber,
        purchaseDate: record.noticeOfLienPurchaseDate,
        receiptDate: record.noticeOfLienReceiptDate,
        lienDate: record.noticeOfLienDate,
        lienholderName: record.noticeOfLienholderName,
        lienholderAddress: record.noticeOfLienholderAddress,
        lienholderCity: record.noticeOfLienholderCity,
        lienholderState: record.noticeOfLienholderState,
        lienholderZip: record.noticeOfLienholderZip,
        subjectToFutureAdvances: record.noticeOfLienSubjectToFutureAdvances,
        refinanceOrNoChange: record.noticeOfLienRefinanceOrNoChange,
      } : undefined,
      // Notice of Sale (Missouri)
      noticeOfSale: record.noticeOfSaleOwnerName ? {
        ownerName: record.noticeOfSaleOwnerName,
        ownerAddress: record.noticeOfSaleOwnerAddress,
        ownerCity: record.noticeOfSaleOwnerCity,
        ownerState: record.noticeOfSaleOwnerState,
        ownerZip: record.noticeOfSaleOwnerZip,
        purchaseDate: record.noticeOfSalePurchaseDate,
        controlNumber: record.noticeOfSaleControlNumber,
      } : undefined,
      // Watercraft
      propulsion: record.propulsion,
      watercraftUse: record.watercraftUse,
      watercraftDescription: record.watercraftDescription,
      emptyWeight: record.emptyWeight,
      isWatercraft: record.isWatercraft,
      purchaseDate: record.purchaseDate,
      enteredDate: record.enteredDate,
      jointOwnership: record.jointOwnership,
    };
  });

  return {
    searchId: data.searchId || crypto.randomUUID(),
    reportId: data.reportId,
    vin: data.vin,
    titleNumber: data.titleNumber,
    stateCode,
    fetchedAt: data.fetchedAt || new Date().toISOString(),
    wasCached: true, // Data from logs is always historical
    billingAmount: 0, // No charge for viewing historical data
    success: data.success !== false,
    titles: transformedTitles,
    errorMessage: data.errorMessage,
    errorCode: data.errorCode,
  };
};

/**
 * Transform a stored registration response from API logs into frontend MvrRegistrationResponse format
 */
export const transformStoredRegistrationResponse = (
  data: StoredRegistrationResponse,
  stateCode: string
): MvrRegistrationResponse => {
  // Transform registration records — handles both formats:
  //   Flat StateDMVRegistrationRecord (new): ownerName, registrationDate, registrationStatus, bodyStyle, ...
  //   Nested IdahoP2PRegistrationObject (legacy): owners[], issueDate, status, body, residenceCounty, ...
  const transformedRegistrations: MvrRegistrationRecord[] = (data.registrations || []).map((record: any) => {
    const isLegacyIdaho = Array.isArray(record.owners);

    const owners: MvrEntity[] = isLegacyIdaho
      ? (record.owners || []).map((o: any) => ({
          name: o.name,
          addressLine1: o.mailingLine1 || o.addressLine1,
          city: o.mailingCity || o.city,
          state: o.mailingState || o.state,
          zip: o.mailingZip || o.zip,
          county: o.county,
          conjunction: o.conjunction,
        }))
      : record.ownerName ? [{
          name: record.ownerName,
          addressLine1: record.ownerAddress,
          city: record.ownerCity,
          state: record.ownerState,
          zip: record.ownerZip,
          county: record.county,
        }] : [];

    return {
      plate: record.plate,
      plateType: record.plateType,
      plateSeriesYear: record.plateSeriesYear,
      vins: isLegacyIdaho ? (record.vins || []) : (record.vin ? [record.vin] : []),
      owners,
      residenceCounty: isLegacyIdaho ? record.residenceCounty : record.county,
      issueDate: isLegacyIdaho ? record.issueDate : record.registrationDate,
      expirationDate: record.expirationDate,
      status: isLegacyIdaho ? (record.statusDescription || record.status) : record.registrationStatus,
      year: record.year,
      make: record.make,
      model: record.model,
      body: isLegacyIdaho ? record.body : record.bodyStyle,
      color: record.color,
      registrationType: record.registrationType,
      registrationUseType: record.registrationUseType,
      grossWeight: record.grossWeight,
    };
  });

  return {
    searchId: data.searchId || crypto.randomUUID(),
    reportId: data.reportId,
    vin: data.vin,
    plate: data.plate,
    stateCode,
    fetchedAt: data.fetchedAt || new Date().toISOString(),
    wasCached: true, // Data from logs is always historical
    billingAmount: 0, // No charge for viewing historical data
    success: data.success !== false,
    registrations: transformedRegistrations,
    errorMessage: data.errorMessage,
    errorCode: data.errorCode,
  };
};

// ============================================================================
// BULK MVR INTERFACES
// ============================================================================

export interface BulkMvrItem {
  vin: string;
  stateCode: string;
  recordType: MvrRecordType;
  referenceNote?: string;
}

export interface BulkMvrResultItem {
  index: number;
  vin: string;
  stateCode: string;
  recordType: string;
  referenceNote?: string;
  success: boolean;
  errorMessage?: string;
  titleCached: boolean;
  registrationCached: boolean;
  billingAmount: number;
  titleResponse?: MvrTitleResponse;
  registrationResponse?: MvrRegistrationResponse;
}

export interface BulkMvrPricingGroup {
  stateCode: string;
  stateName: string;
  recordType: string;
  count: number;
  unitCost: number;
  subtotal: number;
}

export interface BulkMvrResponse {
  batchId: string;
  processedAt: string;
  totalRequested: number;
  totalSucceeded: number;
  totalFailed: number;
  totalCached: number;
  totalBillable: number;
  totalBillingAmount: number;
  pricingBreakdown: BulkMvrPricingGroup[];
  results: BulkMvrResultItem[];
}

export interface BulkMvrPricingResponse {
  breakdown: BulkMvrPricingGroup[];
  totalEstimatedCost: number;
}

// ============================================================================
// BULK MVR API FUNCTIONS
// ============================================================================

/**
 * Execute a bulk MVR search request
 */
export const createBulkMvrReports = async (items: BulkMvrItem[]): Promise<BulkMvrResponse> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.STATE_DMV.BULK_CREATE, {
    method: 'POST',
    body: JSON.stringify({ items }),
  });

  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }

  return await response.json();
};

/**
 * Estimate pricing for a bulk MVR request without executing searches
 */
export const fetchBulkMvrPricing = async (items: BulkMvrItem[]): Promise<BulkMvrPricingResponse> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.STATE_DMV.BULK_PRICING, {
    method: 'POST',
    body: JSON.stringify({ items }),
  });

  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }

  return await response.json();
};

/**
 * Generate and download a combined PDF for bulk MVR results
 */
export const generateBulkMvrPdf = async (items: BulkMvrItem[], requestedBy?: string): Promise<void> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.STATE_DMV.BULK_MVR_PDF, {
    method: 'POST',
    body: JSON.stringify({ items, requestedBy }),
  });

  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }

  const blob = await response.blob();
  const contentDisposition = response.headers.get('content-disposition');
  let filename = `BulkMVR-${new Date().toISOString().slice(0, 10)}.pdf`;
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
    if (filenameMatch && filenameMatch[1]) {
      filename = filenameMatch[1].replace(/['"]/g, '');
    }
  }

  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};

// ============================================================================
// BULK MVR CSV FUNCTIONS
// ============================================================================

/**
 * Valid record type values for CSV parsing
 */
const VALID_RECORD_TYPES = ['title', 'registration', 'both'];

/**
 * Parse bulk MVR items from a CSV file.
 * Expected columns: VIN, State, RecordType, ReferenceNote
 * State can be 2-letter code or full name.
 */
export const parseMvrItemsFromCsv = (csvContent: string): BulkMvrItem[] => {
  if (!csvContent || !csvContent.trim()) return [];

  const lines = csvContent.split(/\r?\n/).filter(line => line.trim());
  if (lines.length === 0) return [];

  const items: BulkMvrItem[] = [];
  const seenKeys = new Set<string>();

  // Check for header row
  const firstLine = lines[0];
  const firstLineLower = firstLine.toLowerCase();
  const firstParts = firstLine.split(',').map(p => p.trim());
  const firstCellLooksLikeVin = /^[A-HJ-NPR-Z0-9]{5,17}$/i.test(firstParts[0]);
  const hasHeaderKeywords = firstLineLower.includes('vin') || firstLineLower.includes('state') || firstLineLower.includes('record');
  const hasHeader = hasHeaderKeywords || !firstCellLooksLikeVin;
  const startIndex = hasHeader ? 1 : 0;

  // Determine column indices
  let vinIndex = 0;
  let stateIndex = 1;
  let recordTypeIndex = 2;
  let refNoteIndex = 3;

  if (hasHeader) {
    const headers = firstLine.split(',').map(h => h.trim().toLowerCase());
    vinIndex = headers.findIndex(h => h === 'vin' || h.includes('vin'));
    stateIndex = headers.findIndex(h => h === 'state' || h === 'statecode' || h.includes('state'));
    recordTypeIndex = headers.findIndex(h => h === 'recordtype' || h === 'type' || h.includes('record'));
    refNoteIndex = headers.findIndex(h => h === 'referencenote' || h === 'reference' || h === 'note' || h.includes('reference'));

    if (vinIndex === -1) {
      throw new Error('CSV must have a "VIN" column header. Please download the template for the correct format.');
    }
    if (stateIndex === -1) {
      throw new Error('CSV must have a "State" column header. Please download the template for the correct format.');
    }
  }

  for (let i = startIndex; i < lines.length; i++) {
    const parts = lines[i].split(',').map(p => p.trim().replace(/['"]/g, ''));
    const vin = (parts[vinIndex] || '').toUpperCase();
    const stateRaw = (parts[stateIndex] || '').trim();
    const recordTypeRaw = recordTypeIndex >= 0 ? (parts[recordTypeIndex] || 'both').toLowerCase().trim() : 'both';
    const refNote = refNoteIndex >= 0 ? (parts[refNoteIndex] || '') : '';

    // Validate VIN
    if (vin.length < 5 || vin.length > 17) continue;

    // Normalize state: accept 2-letter code or full name
    let stateCode = stateRaw.length === 2 ? stateRaw.toUpperCase() : '';
    if (!stateCode) {
      // Try to find state code from name
      const entry = Object.entries(stateNames).find(
        ([, name]) => name.toLowerCase() === stateRaw.toLowerCase()
      );
      if (entry) stateCode = entry[0];
      else continue; // Skip invalid state
    }

    // Normalize record type
    const recordType = VALID_RECORD_TYPES.includes(recordTypeRaw)
      ? (recordTypeRaw as MvrRecordType)
      : 'both';

    // Deduplicate by VIN+State+RecordType
    const key = `${vin}-${stateCode}-${recordType}`;
    if (seenKeys.has(key)) continue;
    seenKeys.add(key);

    items.push({
      vin,
      stateCode,
      recordType,
      referenceNote: refNote.trim() || undefined,
    });
  }

  return items;
};

/**
 * Generate CSV template content for bulk MVR upload
 */
export const generateMvrCsvTemplate = (): string => {
  return 'VIN,State,RecordType,ReferenceNote\n1HGBH41JXMN109186,KY,both,Customer: John Smith\n2HGFC2F59MH123456,ID,title,Invoice #12345\n';
};

/**
 * Download CSV template for bulk MVR upload
 */
export const downloadMvrCsvTemplate = (): void => {
  const content = generateMvrCsvTemplate();
  const blob = new Blob([content], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'MVR_Bulk_Template.csv';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

// ============================================================================
// LEGACY EXPORTS (for backward compatibility)
// Type aliases to maintain compatibility with existing code
// ============================================================================

// Re-export with Idaho-specific names for backward compatibility
export type IdahoP2PEntity = MvrEntity;
export type IdahoP2PTitleObject = MvrTitleRecord;
export type IdahoP2PRegistrationObject = MvrRegistrationRecord;
export type IdahoP2PTitleResponse = MvrTitleResponse;
export type IdahoP2PRegistrationResponse = MvrRegistrationResponse;

// Legacy function alias
export const getSupportedStates = getSupportedStateCodes;

export { getErrorMessage };
